import { useEffect, useRef, useState } from 'react';

interface PerformanceMetrics {
  renderCount: number;
  averageRenderTime: number;
  lastRenderTime: number;
  peakRenderTime: number;
}

export const usePerformanceMonitor = (componentName: string, enabled: boolean = false) => {
  const renderCountRef = useRef(0);
  const renderTimesRef = useRef<number[]>([]);
  const startTimeRef = useRef<number>(0);
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renderCount: 0,
    averageRenderTime: 0,
    lastRenderTime: 0,
    peakRenderTime: 0,
  });

  // Start timing on component render
  useEffect(() => {
    if (!enabled) return;
    
    startTimeRef.current = performance.now();
    renderCountRef.current += 1;
  });

  // End timing after render
  useEffect(() => {
    if (!enabled) return;
    
    const endTime = performance.now();
    const renderTime = endTime - startTimeRef.current;
    renderTimesRef.current.push(renderTime);

    // Keep only last 100 render times for memory efficiency
    if (renderTimesRef.current.length > 100) {
      renderTimesRef.current = renderTimesRef.current.slice(-100);
    }

    const averageRenderTime = renderTimesRef.current.reduce((sum, time) => sum + time, 0) / renderTimesRef.current.length;
    const peakRenderTime = Math.max(...renderTimesRef.current);

    setMetrics({
      renderCount: renderCountRef.current,
      averageRenderTime: Math.round(averageRenderTime * 100) / 100,
      lastRenderTime: Math.round(renderTime * 100) / 100,
      peakRenderTime: Math.round(peakRenderTime * 100) / 100,
    });

    // Log to console in development
    if (process.env.NODE_ENV === 'development' && renderCountRef.current % 10 === 0) {
      console.log(`${componentName} Performance:`, {
        renders: renderCountRef.current,
        avgTime: `${averageRenderTime.toFixed(2)}ms`,
        lastTime: `${renderTime.toFixed(2)}ms`,
        peakTime: `${peakRenderTime.toFixed(2)}ms`,
      });
    }
  }, [enabled, componentName]);

  const resetMetrics = () => {
    renderCountRef.current = 0;
    renderTimesRef.current = [];
    setMetrics({
      renderCount: 0,
      averageRenderTime: 0,
      lastRenderTime: 0,
      peakRenderTime: 0,
    });
  };

  return {
    metrics,
    resetMetrics,
  };
};
